import { Entity } from '@api/api/entity-schema';
import { getTimeTillEndOfDayMs } from '@helpers/utils/dates/getTimeTillEndOfDayMs';
import {
  useInfiniteQuery,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { postEntity } from '@terminal/services/entities/entity-service';

import {
  createEntityQueryOptions,
  QUERY_KEY_ENTITY,
} from './useEntities.utils';

export const QUERY_KEY_ENTITIES = 'QUERY_KEY_ENTITIES';

export const useEntity = (entityTag?: string) =>
  useQuery(createEntityQueryOptions(entityTag));

export const useEntities = ({
  entityTags,
  enabled = true,
  appendFetching = false,
}: {
  entityTags: string[];
  enabled?: boolean;
  appendFetching?: boolean;
}) => {
  const queries = entityTags.map(entityTag => ({
    queryKey: [QUERY_KEY_ENTITY, entityTag],
    queryFn: () => postEntity(entityTag),
    staleTime: getTimeTillEndOfDayMs(),
    enabled,
  }));
  return useQueries({
    queries,
    combine: results => {
      return {
        data: results
          .map(result => {
            if (appendFetching) {
              return {
                ...result.data?.data,
                isFetching: result.isFetching,
              };
            } else {
              return result.data?.data;
            }
          })
          .filter(Boolean) as Entity[],
        isLoading: results.some(
          result => result.isLoading || result.isRefetching
        ),
        isError: !!results.length && results.every(result => result.error),
        isFetched: results.every(result => result.isFetched),
        refetch: () => {
          results.forEach(result => result.refetch());
        },
      };
    },
  });
};

export const useEntitiesTableData = (entities: string[], pageSize = 20) => {
  const queryClient = useQueryClient();
  return useInfiniteQuery({
    queryKey: [
      QUERY_KEY_ENTITIES,
      pageSize,
      ...[...entities].sort((a, b) => a.localeCompare(b)),
    ],
    queryFn: async ({ pageParam }) => {
      const start = pageParam * pageSize;
      const slice = entities.slice(start, start + pageSize);
      const results = await Promise.all(
        slice.map(tag =>
          queryClient
            .ensureQueryData(createEntityQueryOptions(tag))
            .then(res => res.data)
        )
      );
      return results;
    },
    initialPageParam: 0,
    enabled: !!entities.length,
    getNextPageParam: (_lastPage, _allPages, lastPageParam) => {
      const lastPageParamIncrement = lastPageParam + 1;
      const nextStart = lastPageParamIncrement * pageSize;
      return nextStart < entities.length ? lastPageParamIncrement : null;
    },
    staleTime: getTimeTillEndOfDayMs(),
  });
};
